import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  FileText, 
  Calendar,
  Users,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Euro,
  Download,
  RefreshCw,
  Archive,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

export default function ReportsPage({ user }) {
  const navigate = useNavigate();
  const [adminStats, setAdminStats] = useState(null);
  const [fatturatoArchive, setFatturatoArchive] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  
  // Dialog for clients by payment status
  const [clientsDialogOpen, setClientsDialogOpen] = useState(false);
  const [clientsDialogTitle, setClientsDialogTitle] = useState("");
  const [clientsDialogData, setClientsDialogData] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || !!user?.is_admin;
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm") || !!user?.is_admin;
  const isAMM = user?.role === "amm" || isDirettore || (user?.secondary_roles || []).includes("amm") || !!user?.is_admin;
  const canView = isPM || isAMM;

  // Generate month options for the last 12 months
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
      options.push({ value: yearMonth, label });
    }
    return options;
  };

  useEffect(() => {
    if (!canView) {
      navigate("/dashboard");
      return;
    }
    fetchData();
    // Check for auto-snapshot on first of month
    checkAutoSnapshot();
  }, [canView, navigate]);

  useEffect(() => {
    if (selectedMonth && fatturatoArchive.length > 0) {
      const archive = fatturatoArchive.find(a => a.year_month === selectedMonth);
      if (archive) {
        handleViewArchive(selectedMonth);
      } else {
        setSelectedArchive(null);
      }
    }
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      const [statsRes, archiveRes] = await Promise.all([
        apiFetch("/stats/admin"),
        apiFetch("/reports/fatturato/archive")
      ]);

      if (statsRes.ok) setAdminStats(await statsRes.json());
      if (archiveRes.ok) setFatturatoArchive(await archiveRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const checkAutoSnapshot = async () => {
    try {
      await apiFetch("/reports/check-auto-snapshot");
    } catch (error) {
      // Silent fail - not critical
    }
  };

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const response = await apiFetch("/reports/fatturato/snapshot", { method: "POST" });
      if (response.ok) {
        toast.success("Snapshot fatturato creato!");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore nella creazione dello snapshot");
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleViewArchive = async (yearMonth) => {
    setLoadingArchive(true);
    try {
      const response = await apiFetch(`/reports/fatturato/archive/${yearMonth}`);
      if (response.ok) {
        setSelectedArchive(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoadingArchive(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await apiFetch("/reports/fatturato/export/excel");
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `fatturato_report_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Report esportato!");
      }
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  const handleShowClients = async (status) => {
    setClientsDialogTitle(status === "pagati" ? "Clienti Paganti" : "Clienti Non Paganti");
    setClientsDialogOpen(true);
    setLoadingClients(true);
    setClientsDialogData([]);
    
    try {
      const response = await apiFetch(`/reports/clients-by-payment?status=${status}`);
      if (response.ok) {
        setClientsDialogData(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoadingClients(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(value || 0);
  };

  if (!canView) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" />
            Report Fatturato
          </h1>
          <p className="text-muted-foreground mt-1">
            Resoconto fatturato mensile e per PM
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCreateSnapshot}
            disabled={creatingSnapshot}
            data-testid="create-snapshot-btn"
          >
            <Archive className="w-4 h-4 mr-2" />
            {creatingSnapshot ? "Creazione..." : "Salva Snapshot"}
          </Button>
          <Button
            onClick={handleExportExcel}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fatturato Totale</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="total-fatturato">
                  {formatCurrency(adminStats?.total_fatturato)}
                </p>
              </div>
              <Euro className="w-10 h-10 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clienti Totali</p>
                <p className="text-2xl font-bold text-indigo-600" data-testid="total-clients">
                  {adminStats?.total_clients || 0}
                </p>
              </div>
              <Building2 className="w-10 h-10 text-indigo-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-md transition-all"
          onClick={() => handleShowClients("pagati")}
          data-testid="card-clients-pagati"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clienti Pagati</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="clients-pagati">
                  {adminStats?.clients_pagati || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Clicca per dettagli →</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-md transition-all"
          onClick={() => handleShowClients("non_pagati")}
          data-testid="card-clients-non-pagati"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clienti Non Pagati</p>
                <p className="text-2xl font-bold text-red-600" data-testid="clients-non-pagati">
                  {adminStats?.clients_non_pagati || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Clicca per dettagli →</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="current" className="space-y-4">
        <TabsList>
          <TabsTrigger value="current">Mese Corrente</TabsTrigger>
          <TabsTrigger value="archive">Archivio Storico</TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="space-y-4">
          {/* Fatturato per PM */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Fatturato per PM
              </CardTitle>
              <CardDescription>
                Suddivisione del fatturato mensile per Project Manager
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        PM
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Clienti
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Fatturato
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Pagati
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Non Pagati
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminStats?.pm_stats?.map((pm, index) => (
                      <tr 
                        key={pm.pm_id || 'unassigned'} 
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`pm-row-${index}`}
                      >
                        <td className="p-3">
                          <span className={`font-medium ${!pm.pm_id ? 'text-muted-foreground italic' : ''}`}>
                            {pm.pm_name}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary">{pm.num_clients}</Badge>
                        </td>
                        <td className="p-3">
                          <span className="font-semibold text-emerald-600">
                            {formatCurrency(pm.fatturato_mensile)}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {pm.clients_pagati}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {pm.clients_non_pagati > 0 ? (
                            <Badge className="bg-red-100 text-red-700">
                              {pm.clients_non_pagati}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr className="bg-slate-50 font-semibold">
                      <td className="p-3">TOTALE</td>
                      <td className="p-3">
                        <Badge>{adminStats?.total_clients}</Badge>
                      </td>
                      <td className="p-3 text-emerald-600">
                        {formatCurrency(adminStats?.total_fatturato)}
                      </td>
                      <td className="p-3">
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {adminStats?.clients_pagati}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {adminStats?.clients_non_pagati > 0 && (
                          <Badge className="bg-red-100 text-red-700">
                            {adminStats?.clients_non_pagati}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Fatturato per SMM */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                Fatturato per SMM
              </CardTitle>
              <CardDescription>
                Suddivisione del fatturato mensile per Social Media Manager (basato sui clienti assegnati)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        SMM
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Clienti
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Fatturato
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Pagati
                      </th>
                      <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Non Pagati
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminStats?.smm_stats?.map((smm, index) => (
                      <tr 
                        key={smm.smm_id || 'unassigned-smm'} 
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`smm-row-${index}`}
                      >
                        <td className="p-3">
                          <span className={`font-medium ${!smm.smm_id ? 'text-muted-foreground italic' : ''}`}>
                            {smm.smm_name}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary">{smm.num_clients}</Badge>
                        </td>
                        <td className="p-3">
                          <span className="font-semibold text-purple-600">
                            {formatCurrency(smm.fatturato_mensile)}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {smm.clients_pagati}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {smm.clients_non_pagati > 0 ? (
                            <Badge className="bg-red-100 text-red-700">
                              {smm.clients_non_pagati}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr className="bg-slate-50 font-semibold">
                      <td className="p-3">TOTALE</td>
                      <td className="p-3">
                        <Badge>{adminStats?.total_clients}</Badge>
                      </td>
                      <td className="p-3 text-purple-600">
                        {formatCurrency(adminStats?.total_fatturato)}
                      </td>
                      <td className="p-3">
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {adminStats?.clients_pagati}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {adminStats?.clients_non_pagati > 0 && (
                          <Badge className="bg-red-100 text-red-700">
                            {adminStats?.clients_non_pagati}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Fatturato per Content Creator */}
          <RoleFatturatoTable
            title="Fatturato per Content Creator"
            description="Clienti gestiti come Content Creator (Videomaker)"
            icon={<TrendingUp className="w-5 h-5 text-amber-600" />}
            stats={adminStats?.videomaker_stats || []}
            idKey="videomaker_id"
            nameKey="videomaker_name"
            label="Content Creator"
            accentColor="text-amber-600"
            total={adminStats?.total_fatturato}
            totalClients={adminStats?.total_clients}
            totalPagati={adminStats?.clients_pagati}
            totalNonPagati={adminStats?.clients_non_pagati}
            formatCurrency={formatCurrency}
            testIdPrefix="videomaker"
          />

          {/* Fatturato per Editor */}
          <RoleFatturatoTable
            title="Fatturato per Editor"
            description="Clienti gestiti come Editor"
            icon={<TrendingUp className="w-5 h-5 text-sky-600" />}
            stats={adminStats?.editor_stats || []}
            idKey="editor_id"
            nameKey="editor_name"
            label="Editor"
            accentColor="text-sky-600"
            total={adminStats?.total_fatturato}
            totalClients={adminStats?.total_clients}
            totalPagati={adminStats?.clients_pagati}
            totalNonPagati={adminStats?.clients_non_pagati}
            formatCurrency={formatCurrency}
            testIdPrefix="editor"
          />
        </TabsContent>

        <TabsContent value="archive" className="space-y-4">
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Archive className="w-5 h-5 text-indigo-600" />
                    Archivio Storico
                  </CardTitle>
                  <CardDescription>
                    Snapshot mensili del fatturato salvati automaticamente il 1° del mese
                  </CardDescription>
                </div>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-48" data-testid="month-selector">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Seleziona mese..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {fatturatoArchive.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun archivio disponibile</p>
                  <p className="text-sm mt-2">
                    Gli snapshot vengono salvati automaticamente il 1° di ogni mese
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {fatturatoArchive.map((snapshot, index) => {
                    const [year, month] = snapshot.year_month.split("-");
                    return (
                      <Card 
                        key={snapshot.snapshot_id}
                        className={`border cursor-pointer transition-colors hover:border-indigo-300 ${
                          selectedArchive?.year_month === snapshot.year_month 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-slate-200'
                        }`}
                        onClick={() => handleViewArchive(snapshot.year_month)}
                        data-testid={`archive-card-${index}`}
                      >
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold">
                                {MONTH_NAMES[parseInt(month) - 1]} {year}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {snapshot.is_auto ? "Auto" : "Manuale"}
                              </p>
                            </div>
                            <Calendar className="w-5 h-5 text-indigo-600" />
                          </div>
                          {snapshot.data && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <p className="text-lg font-bold text-emerald-600">
                                {formatCurrency(snapshot.data.total_fatturato)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {snapshot.data.total_clients} clienti
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Selected Archive Details */}
              {selectedArchive && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Dettaglio: {MONTH_NAMES[selectedArchive.month - 1]} {selectedArchive.year}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Fatturato</p>
                      <p className="font-bold text-emerald-600">
                        {formatCurrency(selectedArchive.data?.total_fatturato)}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Clienti</p>
                      <p className="font-bold">{selectedArchive.data?.total_clients}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Pagati</p>
                      <p className="font-bold text-emerald-600">{selectedArchive.data?.clients_pagati}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Non Pagati</p>
                      <p className="font-bold text-red-600">{selectedArchive.data?.clients_non_pagati}</p>
                    </div>
                  </div>

                  {/* PM Stats Table */}
                  {selectedArchive.data?.pm_stats && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-2 flex items-center gap-2 text-indigo-600">
                        <Users className="w-4 h-4" />
                        Fatturato per PM
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">PM</th>
                              <th className="text-left p-2">Clienti</th>
                              <th className="text-left p-2">Fatturato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedArchive.data.pm_stats.map((pm, idx) => (
                              <tr key={idx} className="border-b border-slate-100">
                                <td className="p-2">{pm.pm_name}</td>
                                <td className="p-2">{pm.num_clients}</td>
                                <td className="p-2 text-emerald-600 font-medium">
                                  {formatCurrency(pm.fatturato_mensile)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* SMM Stats Table */}
                  {selectedArchive.data?.smm_stats && (
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2 text-purple-600">
                        <TrendingUp className="w-4 h-4" />
                        Fatturato per SMM
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">SMM</th>
                              <th className="text-left p-2">Clienti</th>
                              <th className="text-left p-2">Fatturato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedArchive.data.smm_stats.map((smm, idx) => (
                              <tr key={idx} className="border-b border-slate-100">
                                <td className="p-2">{smm.smm_name}</td>
                                <td className="p-2">{smm.num_clients}</td>
                                <td className="p-2 text-purple-600 font-medium">
                                  {formatCurrency(smm.fatturato_mensile)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog for clients by payment status */}
      <Dialog open={clientsDialogOpen} onOpenChange={setClientsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {clientsDialogTitle.includes("Non") ? (
                <XCircle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              )}
              {clientsDialogTitle}
            </DialogTitle>
          </DialogHeader>
          
          {loadingClients ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              <p className="text-muted-foreground mt-2">Caricamento...</p>
            </div>
          ) : clientsDialogData.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-4">
                Totale: {clientsDialogData.length} clienti - 
                Fatturato: {formatCurrency(clientsDialogData.reduce((sum, c) => sum + (c.importo_mensile || 0), 0))}
              </p>
              {clientsDialogData.map((client, index) => (
                <div 
                  key={client.client_id} 
                  className={`p-3 rounded-lg border ${
                    client.ha_pagato 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{client.name}</p>
                      <p className="text-sm text-muted-foreground">
                        PM: {client.pm_name} | SMM: {client.smm_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${client.ha_pagato ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(client.importo_mensile)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nessun cliente trovato
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// Componente: tabella fatturato per un ruolo specifico (CC/Editor)
// ============================================================
function RoleFatturatoTable({ title, description, icon, stats, idKey, nameKey, label, accentColor, total, totalClients, totalPagati, totalNonPagati, formatCurrency, testIdPrefix }) {
  return (
    <Card className="border border-slate-200">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clienti</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fatturato</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pagati</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Non Pagati</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((row, index) => (
                <tr key={row[idKey] || `unassigned-${testIdPrefix}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${testIdPrefix}-row-${index}`}>
                  <td className="p-3">
                    <span className={`font-medium ${!row[idKey] ? 'text-muted-foreground italic' : ''}`}>
                      {row[nameKey]}
                    </span>
                  </td>
                  <td className="p-3"><Badge variant="secondary">{row.num_clients}</Badge></td>
                  <td className="p-3">
                    <span className={`font-semibold ${accentColor}`}>{formatCurrency(row.fatturato_mensile)}</span>
                  </td>
                  <td className="p-3">
                    <Badge className="bg-emerald-100 text-emerald-700">{row.clients_pagati}</Badge>
                  </td>
                  <td className="p-3">
                    {row.clients_non_pagati > 0 ? (
                      <Badge className="bg-red-100 text-red-700">{row.clients_non_pagati}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold">
                <td className="p-3">TOTALE</td>
                <td className="p-3"><Badge>{totalClients}</Badge></td>
                <td className={`p-3 ${accentColor}`}>{formatCurrency(total)}</td>
                <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700">{totalPagati}</Badge></td>
                <td className="p-3">
                  {totalNonPagati > 0 && (
                    <Badge className="bg-red-100 text-red-700">{totalNonPagati}</Badge>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
